/** Faceted miniature art. Coordinates are projected before baking, never in the simulation. */
import type { Graphics } from 'pixi.js';
import type { BuildingMotif, BuildingType } from '../data/buildings.ts';
import type { UnitVisual } from '../data/units.ts';
import { mix, shade } from '../data/palette.ts';
import type { Vernacular } from './vernacular.ts';
import { roofGeometry } from './roofgeometry.ts';
import ashenMaterials from './ashen-materials.json';
import { gait, type UnitPose } from './unitmotion.ts';

// These are final sRGB paint colors, shared with the Blender material inputs.
// Resolve once; the other vernaculars retain their existing color derivation.
const ASHEN_MATERIALS = Object.fromEntries(Object.entries(ashenMaterials.materials)
  .map(([name, hex]) => [name, Number.parseInt(hex, 16)])) as Record<keyof typeof ashenMaterials.materials, number>;

const EDGE = 0x263238;
type Point = [number, number];
type Vec3 = [number, number, number];
export interface MiniPalette { accent: number; cloth: number; metal: number; rim: number }

function face(g: Graphics, points: number[], color: number, edge = 0.65): void {
  g.poly(points).fill(color);
  if (edge > 0) g.stroke({ color: shade(color, 0.55), width: edge, join: 'round', alpha: 0.8 });
}

/** Building art is projected from local ground coordinates; verticals stay vertical. */
export interface LowPolyBuildingGeometry {
  groundCenterX: number;
  groundCenterY: number;
  /** Actual supported geometry at z=0, excluding dirt and cast shadows. */
  groundFootprint: Array<[number, number]>;
  groundFrontY: number;
  /** Convex hull in authoring pixels, before the texture adds its top padding. */
  silhouette: Array<[number, number]>;
}

/**
 * A 45° azimuth and 30° elevation, normalized so the ground axes are 2:1.
 * The same normalization gives verticals sqrt(2)*cos(30°), not 1. Keeping
 * world heights at 1 made facades 18% shorter than this ground projection.
 */
export function projectTownPoint(x: number, y: number, z: number): Point {
  return [x - y, (x + y) * 0.5 - z * Math.sqrt(1.5)];
}

interface TownFace {
  vertices: Vec3[];
  color: number;
  depth: number;
  edge: number;
  alpha: number;
  silhouette: boolean;
  ground: boolean;
  assembly?: number;
  order: number;
}
interface TownBlock {
  x: number; y: number; z: number; w: number; d: number; h: number; peak: number;
  assembly?: number;
}

/**
 * The two ground axes have slopes +1/2 and -1/2. Buildings use the same
 * projection for their foundations, walls, roof ridges, doors and fittings.
 * Separate masses are ordered in ground depth. Within a roofed mass the body,
 * facade fittings and roof are painted in structural order: an average face
 * depth cannot put a hidden ceiling in front of its own pitched roof.
 */
class TownMesh {
  private readonly faces: TownFace[] = [];
  private readonly assemblies: number[] = [];
  private activeAssembly: number | undefined;
  constructor(readonly width: number, readonly depth: number) {}
  assembly<T>(depth: number, paint: () => T): T {
    const previous = this.activeAssembly;
    this.activeAssembly = this.assemblies.length;
    this.assemblies.push(depth);
    try { return paint(); } finally { this.activeAssembly = previous; }
  }
  within<T>(b: TownBlock, paint: () => T): T {
    const previous = this.activeAssembly;
    this.activeAssembly = b.assembly;
    try { return paint(); } finally { this.activeAssembly = previous; }
  }
  /** Convex crowns can sort their own facets without interleaving wall faces. */
  sorted(paint: () => void): void {
    const start = this.faces.length;
    paint();
    const part = this.faces.splice(start).sort((a, b) => a.depth - b.depth);
    this.faces.push(...part);
  }
  project([x, y, z]: Vec3): Point {
    return projectTownPoint(x - this.width / 2, y - this.depth / 2, z);
  }
  poly(vertices: Vec3[], color: number, edge = 0.45, depth?: number, alpha = 1, silhouette = true, ground = false): void {
    this.faces.push({
      vertices, color, edge, alpha, silhouette, ground,
      depth: depth ?? vertices.reduce((sum, v) => sum + v[0] + v[1] + v[2], 0) / vertices.length,
      assembly: this.activeAssembly, order: this.faces.length,
    });
  }
  stroke(a: Vec3, b: Vec3, color: number, width = 1, depth?: number): void {
    this.poly([a, b], color, width, depth);
  }
  ground(points: Array<[number, number]>, color: number, alpha = 1): void {
    this.poly(points.map(([x, y]) => [x, y, 0] as Vec3), color, 0, -1000, alpha, false, true);
  }
  box(x: number, y: number, z: number, w: number, d: number, h: number, color: number, taper = 0, top = true): TownBlock {
    // Contact belongs to the mass that stands here, not a generic lot-sized
    // dark patch. The two short bands meet the footing with no floating gap.
    if (z === 0) {
      const previous = this.activeAssembly;
      this.activeAssembly = undefined;
      const spread = Math.min(w, d, 6) * 0.16;
      const cast = Math.min(Math.max(h * 0.18, spread), Math.min(w, d) * 0.16);
      this.poly([[x, y + d, 0], [x + w, y + d, 0], [x + w, y, 0],
        [x + w + cast, y, 0], [x + w + cast, y + d + cast * 0.5, 0], [x, y + d + cast * 0.5, 0]],
      0x1d2920, 0, -999, 0.15, false);
      this.poly([[x - spread, y + d, 0], [x + w, y + d, 0], [x + w, y - spread, 0],
        [x + w + spread, y - spread, 0], [x + w + spread, y + d + spread, 0], [x - spread, y + d + spread, 0]],
      0x172019, 0, -998, 0.27, false);
      this.activeAssembly = previous;
    }
    const t = Math.min(w * 0.12, d * 0.12, taper);
    const a: Vec3 = [x + t, y + t, z + h];
    const b: Vec3 = [x + w - t, y + t, z + h];
    const c: Vec3 = [x + w - t, y + d - t, z + h];
    const e: Vec3 = [x + t, y + d - t, z + h];
    // The left-facing facade catches the light; the right-facing facade is cool.
    this.poly([[x, y + d, z], [x + w, y + d, z], c, e], shade(color, 1.04));
    this.poly([[x + w, y + d, z], [x + w, y, z], b, c], shade(color, 0.72));
    if (top) this.poly([a, b, c, e], shade(color, 1.22));
    return { x, y, z, w, d, h, peak: z + h, assembly: this.activeAssembly };
  }
  draw(g: Graphics, W: number, H: number, pad: number): LowPolyBuildingGeometry {
    const projected = this.faces.flatMap((f) => f.vertices.map((v) => this.project(v)));
    const minX = Math.min(...projected.map((p) => p[0]));
    const maxX = Math.max(...projected.map((p) => p[0]));
    const minY = Math.min(...projected.map((p) => p[1]));
    const maxY = Math.max(...projected.map((p) => p[1]));
    // Keep the logical lot centre fixed even for a tall tower or asymmetric mill.
    const cx = W / 2, cy = H / 2;
    const scale = Math.min(
      (W / 2 - 2) / Math.max(-minX, maxX, 1),
      (H / 2 - 2) / Math.max(maxY, 1),
      (H / 2 + pad - 2) / Math.max(-minY, 1),
    );
    const transform = (v: Vec3): Point => {
      const [x, y] = this.project(v);
      return [cx + x * scale, cy + y * scale];
    };
    this.faces.forEach((f, i) => { f.order = i; });
    this.faces.sort((a, b) => {
      const depthA = a.assembly === undefined ? a.depth : this.assemblies[a.assembly];
      const depthB = b.assembly === undefined ? b.depth : this.assemblies[b.assembly];
      return depthA - depthB || a.order - b.order;
    });
    const hullPoints: Point[] = [];
    for (const f of this.faces) {
      const points = f.vertices.map(transform);
      if (f.silhouette) hullPoints.push(...points);
      const flat = points.flat();
      if (points.length === 2) {
        g.moveTo(flat[0], flat[1]).lineTo(flat[2], flat[3]).stroke({
          color: f.color, width: Math.max(0.5, f.edge * scale), alpha: f.alpha, cap: 'round',
        });
      } else {
        g.poly(flat).fill({ color: f.color, alpha: f.alpha });
        if (f.edge > 0) g.stroke({ color: shade(f.color, 0.62), width: Math.max(0.35, f.edge * scale), alpha: 0.65, join: 'round' });
      }
    }
    const groundFootprint = convexHull(this.faces.filter(f => f.silhouette)
      .flatMap(f => f.vertices.filter(v => v[2] === 0).map(transform)));
    return {
      groundCenterX: cx, groundCenterY: cy,
      groundFootprint,
      groundFrontY: groundFootprint.length ? Math.max(...groundFootprint.map(p => p[1])) : cy,
      silhouette: convexHull(hullPoints),
    };
  }
}

function convexHull(points: Point[]): Point[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (a: Point, b: Point, c: Point) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const half = (source: Point[]) => {
    const out: Point[] = [];
    for (const p of source) {
      while (out.length > 1 && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop();
      out.push(p);
    }
    return out;
  };
  const low = half(sorted), high = half(sorted.reverse());
  low.pop(); high.pop();
  return [...low, ...high];
}

function facadeDepth(b: TownBlock, side = false): number {
  return side ? b.x + b.w + b.y + b.d / 2 + b.z + b.h / 2 : b.x + b.w / 2 + b.y + b.d + b.z + b.h / 2;
}

/** Openings lie on the facade plane, including their sills and arched heads. */
function townOpening(m: TownMesh, b: TownBlock, k: Vernacular, offset: number, z: number, w: number, h: number, lit = false, side = false): void {
  const at = (u: number, height: number): Vec3 => side ? [b.x + b.w + 0.03, b.y + u, height] : [b.x + u, b.y + b.d + 0.03, height];
  const shoulder = k.opening === 'lancet' ? h * 0.3 : k.opening === 'arch' ? h * 0.16 : 0;
  const dep = facadeDepth(b, side) + 0.06;
  m.poly([at(offset, z), at(offset + w, z), at(offset + w, z + h - shoulder), at(offset + w / 2, z + h), at(offset, z + h - shoulder)], lit ? 0xe2b66c : k.id === 'ashen' ? ASHEN_MATERIALS.shadow : 0x303932, 0.5, dep);
  m.stroke(at(offset - 0.5, z), at(offset + w + 0.5, z), k.id === 'ashen' ? ASHEN_MATERIALS.stone_2 : shade(k.wallLit, 1.8), 0.9, dep + 0.01);
}

function townFront(m: TownMesh, b: TownBlock, k: Vernacular, doors = 1, windows = true, framed = false): void {
  const timber = k.id === 'ashen' ? ASHEN_MATERIALS.timber : mix(shade(k.timber, 1.6), 0x75553d, 0.18);
  const dh = Math.min(b.h * 0.68, b.w * 0.36);
  for (let i = 0; i < doors; i++) {
    const dw = Math.min(b.w / (doors * 3.6), dh * 0.65);
    townOpening(m, b, k, b.w * ((i + 0.5) / doors) - dw / 2, b.z + 0.8, dw, dh);
  }
  if (windows) {
    for (const t of [0.17, 0.8]) townOpening(m, b, k, b.w * t - b.w * 0.035, b.z + b.h * 0.55, b.w * 0.075, b.h * 0.2, true);
    townOpening(m, b, k, b.d * 0.42, b.z + b.h * 0.47, b.d * 0.14, b.h * 0.25, false, true);
  }
  const beam = (side: boolean, u0: number, z0: number, u1: number, z1: number, color: number, weight = 0.85) => {
    const at = (u: number, z: number): Vec3 => side ? [b.x + b.w + 0.05, b.y + u, b.z + z] : [b.x + u, b.y + b.d + 0.05, b.z + z];
    m.stroke(at(u0, z0), at(u1, z1), color, weight, facadeDepth(b, side) + 0.1);
  };
  if (framed || ['daub', 'log', 'plank', 'bark', 'hide'].includes(k.fabric)) {
    for (const side of [false, true]) {
      const span = side ? b.d : b.w;
      for (const t of [0.03, 0.5, 0.97]) beam(side, span * t, 0, span * t, b.h, timber, framed ? 2.2 : 1.2);
      beam(side, 0, b.h * 0.28, span, b.h * 0.28, timber, 1.6);
      beam(side, 0, b.h * 0.98, span, b.h * 0.98, timber, 2);
      if (framed || k.fabric === 'daub' || k.fabric === 'plank') {
        beam(side, span * 0.05, b.h * 0.48, span * 0.44, b.h * 0.96, timber);
        beam(side, span * 0.56, b.h * 0.96, span * 0.95, b.h * 0.48, timber);
      }
    }
  } else if (k.fabric === 'ashlar' || k.fabric === 'rubble') {
    for (const side of [false, true]) {
      const span = side ? b.d : b.w;
      for (const t of [0.3, 0.62]) beam(side, 0, b.h * t, span, b.h * t, k.id === 'ashen' ? ASHEN_MATERIALS.stone_2 : shade(k.wallLit, 1.3), 0.45);
    }
  }
}

/** A lower, open porch gives the front a second roof line and visible supports. */
function townPorch(m: TownMesh, b: TownBlock, k: Vernacular, width = 0.52, reach = 0.25): void {
  const x = b.x + b.w * (1 - width) / 2, y = b.y + b.d - 0.8;
  const w = b.w * width, d = b.d * reach;
  const timber = k.id === 'ashen' ? ASHEN_MATERIALS.timber : mix(shade(k.timber, 1.8), 0x856345, 0.24);
  const back = b.z + b.h * 0.79, front = back - d * 0.46;
  const roof = k.id === 'ashen' ? mix(k.roofLit, k.roof, 0.2) : mix(shade(k.roofLit, 1.8), 0xb9a27b, 0.3);
  m.assembly(x + y + (w + d) / 2, () => {
    m.box(x - 1.2, y, 0, w + 2.4, d + 1.4, 0.8, k.id === 'ashen' ? ASHEN_MATERIALS.stone_1 : mix(k.wall, 0xad9c7a, 0.52));
    for (const px of [x, x + w - 1.7]) {
      m.box(px, y + d - 1.7, 0, 1.7, 1.7, front, timber);
      m.stroke([px + 0.85, y + d, front * 0.73], [px + (px === x ? w * 0.21 : -w * 0.16), y + d, front], timber, 1.5);
    }
    m.stroke([x, y + d, front], [x + w, y + d, front], timber, 2.3);
    m.poly([[x - 2, y - 1, back], [x + w + 2, y - 1, back], [x + w + 2, y + d + 2, front], [x - 2, y + d + 2, front]], roof);
    for (const t of [0.32, 0.67]) {
      m.stroke([x - 2, y - 1 + (d + 3) * t, back + (front - back) * t],
        [x + w + 2, y - 1 + (d + 3) * t, back + (front - back) * t], shade(roof, 0.77), 0.8);
    }
    m.stroke([x - 2, y + d + 2, front], [x + w + 2, y + d + 2, front], timber, 2.2);
  });
}

/** Roof planes are generous enough to reveal the elevated view at tactical zoom. */
function townRoof(m: TownMesh, b: TownBlock, k: Vernacular, rise: number, civic: boolean): void {
  const over = Math.min(b.w, b.d) * (0.04 + k.eave * 0.08);
  const x = b.x - over, y = b.y - over;
  const w = b.w + 2 * over, d = b.d + 2 * over, z = b.z + b.h;
  const r = rise * (0.65 + k.pitch * 0.35);
  const lit = k.id === 'ashen' ? k.roofLit : mix(shade(k.roofLit, 1.6), 0xb5a98b, 0.18);
  const dark = k.id === 'ashen' ? k.roof : mix(shade(k.roof, 1.36), 0x65746b, 0.16);
  const crown = civic ? k.crown : 'gable';
  b.peak = z + r;
  if (crown === 'flat' || crown === 'deck' || crown === 'battlement') {
    const cap = m.box(x, y, z, w, d, 2.2, k.id === 'ashen' ? ASHEN_MATERIALS.stone_1 : mix(k.wallLit, 0xb5af97, 0.36));
    b.peak = cap.peak;
    m.poly([[x + 2, y + 2, cap.peak + 0.02], [x + w - 2, y + 2, cap.peak + 0.02], [x + w - 2, y + d - 2, cap.peak + 0.02], [x + 2, y + d - 2, cap.peak + 0.02]], k.id === 'ashen' ? ASHEN_MATERIALS.wood : shade(dark, 0.83));
    if (crown === 'battlement') {
      const mw = Math.min(w, d) * 0.12;
      const masonry = k.id === 'ashen' ? ASHEN_MATERIALS.stone_1 : lit;
      for (const t of [0.06, 0.34, 0.62, 0.9]) {
        m.box(x + (w - mw) * t, y, cap.peak, mw, mw, 3.2, masonry);
        m.box(x + (w - mw) * t, y + d - mw, cap.peak, mw, mw, 3.2, masonry);
      }
      for (const t of [0.34, 0.67]) {
        m.box(x, y + (d - mw) * t, cap.peak, mw, mw, 3.2, masonry);
        m.box(x + w - mw, y + (d - mw) * t, cap.peak, mw, mw, 3.2, masonry);
      }
      b.peak += 3.2;
    }
    return;
  }
  if (['dome', 'cone', 'cap', 'spire'].includes(crown)) {
    const cx = x + w / 2, cy = y + d / 2;
    const reach = r * (crown === 'spire' ? 1.45 : crown === 'cap' ? 0.62 : 1);
    const ring: Vec3[] = [];
    const mid: Vec3[] = [];
    for (let i = 0; i < 8; i++) {
      const a = i * Math.PI / 4;
      ring.push([cx + Math.cos(a) * w * 0.56, cy + Math.sin(a) * d * 0.56, z]);
      mid.push([cx + Math.cos(a) * w * 0.39, cy + Math.sin(a) * d * 0.39, z + reach * 0.68]);
    }
    m.sorted(() => {
      for (let i = 0; i < 8; i++) {
        const next = (i + 1) % 8;
        const color = mix(dark, lit, (1 + Math.sin(i * Math.PI / 4)) * 0.42);
        if (crown === 'dome' || crown === 'cap') {
          m.poly([ring[i], ring[next], mid[next], mid[i]], color);
          m.poly([mid[i], mid[next], [cx, cy, z + reach]], shade(color, 1.16));
        } else m.poly([ring[i], ring[next], [cx, cy, z + reach]], color);
      }
    });
    b.peak = z + reach;
    if (crown === 'spire') m.stroke([cx, cy, b.peak], [cx, cy, b.peak + 4], shade(k.trim, 1.8), 1);
    return;
  }
  if (crown === 'lean') {
    m.poly([[x, y, z + r], [x + w, y, z + r], [x + w, y + d, z], [x, y + d, z]], lit);
    m.poly([[x + w, y, z], [x + w, y, z + r], [x + w, y + d, z]], dark);
    return;
  }
  townGableRoof(m, b, k, lit, dark, crown);
}

function townGableRoof(m: TownMesh, b: TownBlock, k: Vernacular, lit: number, dark: number, crown: Vernacular['crown']): void {
  // Gables span the short body axis, including the eaves. A long hall must
  // not stretch a shallow cap across its longest dimension.
  const profile = roofGeometry(b, k.pitch, k.eave);
  const { x, y, w, d, z, rise: r, point, farVisible } = profile;
  const poly = (vertices: Vec3[], color: number, edge = 0.45) => m.poly(vertices.map(point), color, edge);
  const stroke = (a: Vec3, b: Vec3, color: number, width = 1, depth?: number) => m.stroke(point(a), point(b), color, width, depth);
  b.peak = z + r;
  const ridgeX = x + w / 2;
  const a: Vec3 = [x, y, z], c: Vec3 = [x + w, y, z], e: Vec3 = [x + w, y + d, z], f: Vec3 = [x, y + d, z];
  const p: Vec3 = [ridgeX, y, z + r], q: Vec3 = [ridgeX, y + d, z + r];
  if (farVisible) poly([a, p, q, f], lit);
  const near = mix(lit, dark, 0.36);
  poly([p, c, e, q], near);
  const slope = (t: number, u: number): Vec3 => [ridgeX + w * 0.5 * t, y + d * u, z + r * (1 - t)];
  // Broad shingle courses follow the actual slope, with staggered joints.
  for (let row = 0; row < 5; row++) {
    const t0 = row / 5, t1 = (row + 1) / 5;
    poly([slope(t0, 0), slope(t1, 0), slope(t1, 1), slope(t0, 1)], shade(near, row % 2 ? 0.96 : 1.04), 0);
    stroke(slope(t1, 0), slope(t1, 1), shade(near, 0.72), 0.8);
    for (let col = 1; col < 7; col++) {
      const u = (col - (row % 2) * 0.5) / 7;
      stroke(slope(t0 + 0.015, u), slope(t1 - 0.015, u), shade(near, 0.82), 0.45);
    }
  }
  if (farVisible) for (const t of [0.3, 0.6, 0.85]) {
    stroke([x + w * 0.5 * t, y, z + r * t], [x + w * 0.5 * t, y + d, z + r * t], shade(lit, 0.83), 0.65);
  }
  const timberGable = ['rubble', 'daub', 'plank', 'log'].includes(k.fabric);
  const gable = k.id === 'ashen' ? ASHEN_MATERIALS.plaster_light : timberGable ? mix(shade(k.timber, 1.1), 0x504734, 0.25) : mix(shade(k.wallLit, 1.65), 0xb6a787, 0.24);
  poly([f, e, q], gable);
  const gableDepth = (x + w / 2) + (y + d) + z + r / 3;
  const beam = k.id === 'ashen' ? ASHEN_MATERIALS.timber_edge : mix(shade(k.timber, 1.8), 0xb29065, 0.2);
  for (const t of [0.18, 0.34, 0.66, 0.82]) {
    const top = z + r * (1 - Math.abs(t * 2 - 1));
    stroke([x + w * t, y + d, z], [x + w * t, y + d, top], k.id === 'ashen' ? ASHEN_MATERIALS.timber : shade(gable, 0.67), 0.7);
  }
  stroke(f, q, beam, 2.6, gableDepth + 0.1);
  stroke(q, e, beam, 2.6, gableDepth + 0.1);
  stroke(f, e, beam, 2.1, gableDepth + 0.1);
  stroke([ridgeX, y + d, z], q, beam, 1.9, gableDepth + 0.11);
  stroke([x + w * 0.2, y + d, z + r * 0.4], [x + w * 0.8, y + d, z + r * 0.4], beam, 1.6);
  stroke(p, q, k.id === 'ashen' ? ASHEN_MATERIALS.roof_2 : mix(lit, 0xe4d7b5, 0.3), 2.1);
  poly([c, e, [e[0], e[1], z - 1.5], [c[0], c[1], z - 1.5]], shade(beam, 0.63), 0);
  if (crown === 'pagoda') {
    const [ux, uy] = point([x + w * 0.29, y + d * 0.27, 0]);
    const upper = m.box(ux, uy, z + r * 0.53, profile.alongX ? d * 0.46 : w * 0.42, profile.alongX ? w * 0.42 : d * 0.46, r * 0.23, shade(k.wallLit, 1.65), 0, false);
    townRoof(m, upper, { ...k, crown: 'gable', eave: k.eave + 0.25 }, r * 0.58, true);
    b.peak = upper.peak;
  } else if (crown === 'branch' || crown === 'rib') {
    for (const end of [y, y + d]) {
      for (const side of [-1, 1]) {
        stroke([ridgeX, end, z + r], [ridgeX + side * w * 0.25, end, z + r * 1.25], beam, 1.5);
        stroke([ridgeX + side * w * 0.25, end, z + r * 1.25], [ridgeX + side * w * 0.2, end, z + r * 1.42], shade(k.trim, 1.7), 1);
      }
    }
    b.peak = z + r * 1.42;
  }
}

function townFlame(m: TownMesh, x: number, y: number, z: number, size: number, depth?: number): void {
  m.poly([[x - size * 0.6, y, z], [x + size * 0.6, y, z], [x + size * 0.28, y, z + size * 0.8], [x, y, z + size * 1.6], [x - size * 0.13, y, z + size * 0.65], [x - size * 0.4, y, z + size]], 0xe59745, 0, depth);
  m.poly([[x - size * 0.2, y + 0.02, z], [x + size * 0.3, y + 0.02, z], [x, y + 0.02, z + size]], 0xffdfa0, 0, depth === undefined ? undefined : depth + 0.1);
}

function townWheel(m: TownMesh, x: number, y: number, z: number, r: number, color: number, cog = false, depth?: number): void {
  const ring: Vec3[] = [];
  for (let i = 0; i < 24; i++) {
    const a = i * Math.PI / 12;
    const radius = r * (cog && i % 3 === 0 ? 1.15 : 1);
    ring.push([x + Math.cos(a) * radius, y, z + Math.sin(a) * radius]);
  }
  m.poly(ring, color, 0.6, depth);
  m.poly([[x - r * 0.2, y + 0.02, z - r * 0.2], [x + r * 0.2, y + 0.02, z - r * 0.2], [x + r * 0.2, y + 0.02, z + r * 0.2], [x - r * 0.2, y + 0.02, z + r * 0.2]], EDGE, 0, depth === undefined ? undefined : depth + 0.1);
}

function townMotif(m: TownMesh, k: Vernacular, kind: BuildingMotif, b: TownBlock): void {
  const s = Math.max(2, Math.min(b.w, b.d) * 0.13);
  const x = b.x + b.w * 0.68, y = b.y + b.d + 0.2, z = b.z + b.h;
  const timber = k.id === 'ashen' ? ASHEN_MATERIALS.timber : shade(k.timber, 1.6), metal = shade(k.trim, 1.8);
  switch (kind) {
    case 'chimney': case 'chimneys': {
      for (let i = 0; i < (kind === 'chimneys' ? 3 : 1); i++) {
        const stack = m.box(b.x + b.w * (0.19 + i * 0.24), b.y + b.d * 0.32, b.peak - s * 1.4, s, s, s * 2.1, k.id === 'ashen' ? ASHEN_MATERIALS.stone_1 : mix(k.wallLit, 0xc3b599, 0.35));
        m.box(stack.x - 0.5, stack.y - 0.5, stack.peak, s + 1, s + 1, 0.8, k.id === 'ashen' ? ASHEN_MATERIALS.iron : metal);
        m.poly([[stack.x + 0.2, stack.y + 0.2, stack.peak + 0.82], [stack.x + s - 0.2, stack.y + 0.2, stack.peak + 0.82], [stack.x + s - 0.2, stack.y + s - 0.2, stack.peak + 0.82], [stack.x + 0.2, stack.y + s - 0.2, stack.peak + 0.82]], EDGE, 0);
      }
      break;
    }
    case 'lantern': case 'cupola': case 'orrery': {
      const cap = m.box(b.x + b.w * 0.39, b.y + b.d * 0.37, b.peak - s * 0.6, s * 1.7, s * 1.7, s * 1.25, k.id === 'ashen' ? ASHEN_MATERIALS.plaster : shade(k.wallLit, 1.6), 0, false);
      townOpening(m, cap, k, s * 0.5, cap.z + s * 0.2, s * 0.65, s * 0.8, true);
      townRoof(m, cap, { ...k, crown: kind === 'lantern' ? 'gable' : 'dome' }, s * 0.9, true);
      if (kind === 'orrery') {
        townWheel(m, cap.x + s * 0.85, cap.y + s * 0.85, cap.peak + s, s, metal);
        m.stroke([cap.x + s * 0.85, cap.y + s * 0.85, cap.peak], [cap.x + s * 0.85, cap.y + s * 0.85, cap.peak + s * 2.1], metal, 0.8);
      }
      break;
    }
    case 'sign':
      m.stroke([b.x + 1, y, z * 0.5], [b.x + 1, y, z + s], timber, 1.3);
      m.stroke([b.x + 1, y, z + s], [b.x - s * 1.3, y, z + s], timber, 1.3);
      m.box(b.x - s * 1.3, y, z - s * 0.35, s * 1.6, 0.7, s, 0x977450);
      break;
    case 'crane':
      m.box(x, b.y + b.d * 0.55, b.peak - s, 1.7, 1.7, s * 3, timber);
      m.stroke([x, b.y + b.d * 0.55, b.peak + s * 2], [x, y + s * 2, b.peak + s * 1.2], timber, 1.8);
      m.stroke([x, b.y + b.d * 0.55, b.peak], [x, y + s * 2, b.peak + s * 1.2], timber, 1);
      m.stroke([x, y + s * 2, b.peak + s * 1.2], [x, y + s * 2, z * 0.45], EDGE, 0.6);
      m.box(x - s * 0.4, y + s * 1.6, z * 0.3, s * 0.8, s * 0.8, s * 0.7, timber);
      break;
    case 'barrels':
      for (let i = 0; i < 3; i++) {
        const c = m.box(b.x + b.w - s * (2.5 - i * 0.85), y + (i % 2) * s * 0.5, 0.5, s * 0.8, s * 0.8, s * 1.1, timber);
        m.stroke([c.x, c.y + c.d + 0.03, s * 0.65], [c.x + c.w, c.y + c.d + 0.03, s * 0.65], metal, 0.6, facadeDepth(c) + 0.1);
      }
      break;
    case 'antlers':
      for (const side of [-1, 1]) {
        m.stroke([x, y, z + s * 0.2], [x + side * s * 1.3, y, z + s * 1.5], 0xd8cfaa, 1.5);
        m.stroke([x + side * s * 0.75, y, z + s], [x + side * s * 0.65, y, z + s * 1.8], 0xd8cfaa, 1);
      }
      break;
    case 'still':
      m.box(x - s, y + s * 0.3, 0, s * 1.8, s * 1.5, s * 2.1, metal, s * 0.25);
      m.stroke([x, y + s, s * 2.2], [x + s * 2, y + s, s * 2.2], metal, 1.2);
      for (let i = 0; i < 3; i++) townWheel(m, x + s * 1.6, y + s, s * (0.5 + i * 0.5), s * 0.45, metal);
      break;
    case 'cog': townWheel(m, x, y, z * 0.7, s * 1.3, metal, true, facadeDepth(b) + 0.2); break;
    case 'scales':
      m.stroke([x, y, z], [x, y, z + s * 2.1], metal, 1.2);
      m.stroke([x - s * 1.5, y, z + s * 1.5], [x + s * 1.5, y, z + s * 1.5], metal, 1);
      for (const side of [-1, 1]) {
        m.stroke([x + side * s * 1.3, y, z + s * 1.5], [x + side * s * 1.3, y, z + s * 0.45], metal, 0.6);
        m.poly([[x + side * s * 1.3 - s * 0.5, y, z + s * 0.45], [x + side * s * 1.3, y, z + s * 0.1], [x + side * s * 1.3 + s * 0.5, y, z + s * 0.45]], metal);
      }
      break;
    case 'cages':
      for (const t of [0.24, 0.66]) {
        const c = m.box(b.x + b.w * t, y + 1, 0.5, s * 1.4, s, s * 1.7, EDGE);
        for (let i = 0; i < 4; i++) m.stroke([c.x + i * s * 0.44, c.y + c.d + 0.03, c.z], [c.x + i * s * 0.44, c.y + c.d + 0.03, c.peak], metal, 0.55, facadeDepth(c) + 0.1);
      }
      break;
    case 'rack':
      for (let i = 0; i < 4; i++) m.stroke([b.x + i * s * 0.7, y + 2, 0.4], [b.x + i * s * 0.7 + 2, y, s * 3.1], timber, 1);
      m.stroke([b.x, y + 0.4, s * 2.1], [b.x + s * 2.4, y + 0.4, s * 2.1], metal, 1.3);
      break;
    case 'colonnade':
      for (let i = 0; i < 4; i++) m.box(b.x + b.w * (0.05 + i * 0.29), y, 0, s * 0.7, s, b.h * 0.78, shade(k.wallLit, 1.85));
      m.box(b.x, y - 0.3, b.h * 0.78, b.w, s * 1.3, s * 0.7, shade(k.wallLit, 1.7));
      break;
    default: break;
  }
}

/** Every catalogue silhouette shares one elevated isometric projection. */
type TownHouse = (x: number, y: number, w: number, d: number, height: number, civic?: boolean, doors?: number) => TownBlock;

/** Production yards are identified by their working apparatus, not just a sign. */
function townIndustry(m: TownMesh, k: Vernacular, id: string, lx: number, ly: number, h: number, house: TownHouse): TownBlock | null {
  const s = Math.min(lx, ly);
  const timber = k.id === 'ashen' ? ASHEN_MATERIALS.timber : mix(shade(k.timber, 1.7), 0x9c7448, 0.3);
  const stone = k.id === 'ashen' ? ASHEN_MATERIALS.stone_1 : mix(shade(k.wallLit, 1.6), 0xa6987e, 0.4);
  const iron = 0x73818a, brass = 0xbca06b;
  const at = (x: number, y: number, z: number): Vec3 => [x * lx, y * ly, z * s];
  const beam = (a: Vec3, b: Vec3, color = timber, width = 2.2) => m.stroke(at(...a), at(...b), color, s * width / 100);
  const block = (x: number, y: number, z: number, w: number, d: number, height: number, color: number) => m.box(x * lx, y * ly, z * s, w * lx, d * ly, height * s, color);
  const yard = <T>(x: number, y: number, paint: () => T): T => m.assembly(x * lx + y * ly, paint);
  const shed = (x: number, y: number, w: number, d: number, stature = 0.85) => house(x * lx, y * ly, w * lx, d * ly, h * stature);
  const stack = (x: number, y: number, height: number, width = 0.11) => yard(x + width / 2, y + width / 2, () => {
    const chimney = block(x, y, 0, width, width, height, mix(stone, 0x94694f, 0.35));
    for (const z of [height * 0.45, height * 0.78]) block(x - 0.009, y - 0.009, z, width + 0.018, width + 0.018, 0.022, iron);
    block(x - 0.015, y - 0.015, height, width + 0.03, width + 0.03, 0.028, stone);
    m.poly([at(x + 0.012, y + 0.012, height + 0.029), at(x + width - 0.012, y + 0.012, height + 0.029), at(x + width - 0.012, y + width - 0.012, height + 0.029), at(x + 0.012, y + width - 0.012, height + 0.029)], 0x222b2d, 0);
    return chimney;
  });
  const crystal = (x: number, y: number, z: number, radius: number, height: number, color: number) => {
    const ring: Vec3[] = Array.from({ length: 6 }, (_, i) => at(x + Math.cos(i * Math.PI / 3) * radius * s / lx, y + Math.sin(i * Math.PI / 3) * radius * s / ly, z + height * 0.4));
    m.sorted(() => {
      for (let i = 0; i < ring.length; i++) {
        const next = (i + 1) % ring.length, tint = shade(color, 0.72 + (i % 4) * 0.15);
        m.poly([at(x, y, z), ring[next], ring[i]], shade(tint, 0.75));
        m.poly([ring[i], ring[next], at(x, y, z + height)], tint);
      }
    });
  };
  const ring = (x: number, y: number, z: number, radius: number, color: number, cog = false) => {
    // A real open rim: sky/roof remains visible through the centre.
    const point = (angle: number, r: number, offset = 0): Vec3 => at(x + Math.cos(angle) * r * s / lx, y + offset, z + Math.sin(angle) * r);
    const count = cog ? 32 : 16;
    for (let i = 0; i < count; i++) {
      const a = i * Math.PI * 2 / count, b = (i + 1) * Math.PI * 2 / count;
      const r0 = radius * (cog && i % 4 < 2 ? 1.12 : 1), r1 = radius * (cog && (i + 1) % 4 < 2 ? 1.12 : 1);
      m.poly([point(a, r0), point(b, r1), point(b, r1, -0.018), point(a, r0, -0.018)], shade(color, 0.63), 0.25);
      m.poly([point(a, r0), point(b, r1), point(b, radius * 0.67), point(a, radius * 0.67)], shade(color, 0.88 + Math.sin(a) * 0.18), 0.25);
    }
  };
  const rack = (x: number, y: number, weapons: boolean) => yard(x + 0.14, y, () => {
    for (const dx of [0, 0.29]) block(x + dx, y, 0, 0.026, 0.03, 0.25, timber);
    block(x - 0.015, y, 0.12, 0.345, 0.025, 0.035, timber);
    for (let i = 0; i < 4; i++) {
      const px = x + 0.04 + i * 0.07, height = weapons ? 0.34 - (i % 2) * 0.055 : 0.22 + (i % 2) * 0.05;
      beam([px, y + 0.037, 0.025], [px + 0.016, y + 0.02, height], weapons ? timber : iron, 1.3);
      if (weapons) {
        m.poly([at(px - 0.01, y + 0.02, height - 0.01), at(px + 0.016, y + 0.02, height + 0.075), at(px + 0.04, y + 0.02, height - 0.01)], 0xcbd5cb);
      } else block(px - 0.023, y + 0.01, height - 0.025, 0.08, 0.03, i % 2 ? 0.035 : 0.055, iron);
    }
  });
  switch (id) {
    case 'quarry': {
      // This is the working yard beside the deposit: exposed cutting beds and
      // squared stock stay low so the timber hoist owns the silhouette.
      const bed = yard(0.45, 0.37, () => {
        const cut = 0xb3b5a3;
        const base = block(0.14, 0.18, 0, 0.65, 0.42, 0.06, cut);
        block(0.17, 0.2, 0.06, 0.53, 0.18, 0.07, cut);
        block(0.19, 0.405, 0.06, 0.38, 0.105, 0.025, 0xc9c9b1);
        for (const x of [0.29, 0.42, 0.55]) beam([x, 0.202, 0.132], [x, 0.38, 0.132], 0x798276, 0.6);
        beam([0.17, 0.381, 0.094], [0.7, 0.381, 0.094], 0x7d877c, 0.7);
        return base;
      });
      yard(0.66, 0.4, () => {
        block(0.575, 0.17, 0.13, 0.19, 0.19, 0.04, stone);
        block(0.635, 0.215, 0.17, 0.065, 0.075, 0.45, timber);
        block(0.62, 0.115, 0.6, 0.095, 0.66, 0.065, timber);
        beam([0.665, 0.265, 0.21], [0.665, 0.575, 0.6], timber, 3.3);
        beam([0.665, 0.265, 0.55], [0.665, 0.745, 0.64], 0x4c4639, 0.9);
        townWheel(m, lx * 0.67, ly * 0.3, s * 0.32, s * 0.073, 0xb89968);
        beam([0.665, 0.73, 0.625], [0.665, 0.73, 0.2], 0x473e31, 0.9);
        const load = block(0.58, 0.645, 0.12, 0.17, 0.17, 0.075, 0xc7c7b4);
        beam([0.58, 0.73, 0.197], [0.665, 0.73, 0.24], iron, 0.9);
        beam([0.75, 0.73, 0.197], [0.665, 0.73, 0.24], iron, 0.9);
        beam([load.x / lx, 0.735, 0.12], [load.x / lx + 0.17, 0.735, 0.12], iron, 0.7);
      });
      yard(0.3, 0.77, () => {
        for (const y of [0.64, 0.805]) for (const x of [0.135, 0.31]) block(x, y, 0, 0.145, 0.135, 0.075, 0xbac1b0);
        for (const x of [0.17, 0.325]) block(x, 0.675, 0.075, 0.13, 0.135, 0.075, 0xc5cbb9);
        beam([0.17, 0.84, 0.078], [0.34, 0.73, 0.17], timber, 1.5);
        beam([0.295, 0.75, 0.17], [0.39, 0.75, 0.17], iron, 2.4);
      });
      return bed;
    }
    case 'iron_mine': {
      // An open headframe, rather than a hill-shaped roof, marks extraction.
      // Its open shaft, rails and loaded cart connect the tall frame to work.
      yard(0, 0, () => {
        block(0.29, 0.2, 0, 0.43, 0.4, 0.027, 0x746956);
        m.poly([at(0.36, 0.25, 0.028), at(0.64, 0.25, 0.028), at(0.64, 0.54, 0.028), at(0.36, 0.54, 0.028)], 0x202c2b, 0);
        for (const y of [0.55, 0.66, 0.77, 0.88]) block(0.32, y, 0.01, 0.36, 0.026, 0.018, timber);
        for (const x of [0.375, 0.605]) beam([x, 0.35, 0.036], [x, 0.94, 0.036], 0x8c9a99, 1.6);
      });
      const frame = yard(0.51, 0.36, () => {
        const base = block(0.275, 0.16, 0, 0.07, 0.09, 0.07, stone);
        for (const x of [0.295, 0.655]) {
          block(x, 0.185, 0.035, 0.05, 0.05, 0.61, timber);
          beam([x + 0.025, 0.205, 0.15], [x + 0.025, 0.49, 0.6], timber, 2.5);
        }
        beam([0.32, 0.21, 0.16], [0.68, 0.21, 0.52], timber, 2.1);
        beam([0.32, 0.21, 0.52], [0.68, 0.21, 0.16], timber, 2.1);
        for (const x of [0.27, 0.68]) {
          block(x - 0.02, 0.515, 0, 0.09, 0.09, 0.07, stone);
          block(x, 0.53, 0.035, 0.05, 0.05, 0.61, timber);
        }
        block(0.25, 0.165, 0.615, 0.5, 0.435, 0.055, timber);
        for (const y of [0.22, 0.31, 0.4, 0.49]) beam([0.253, y, 0.672], [0.747, y, 0.672], 0xa58a60, 0.8);
        block(0.29, 0.17, 0.67, 0.055, 0.37, 0.08, timber);
        block(0.655, 0.17, 0.67, 0.055, 0.37, 0.08, timber);
        townWheel(m, lx * 0.5, ly * 0.43, s * 0.747, s * 0.13, 0x8b9695);
        beam([0.5, 0.43, 0.755], [0.5, 0.43, 0.1], 0x3d4037, 0.9);
        beam([0.295, 0.565, 0.21], [0.295, 0.565, 0.6], 0xb49b70, 1.1);
        return base;
      });
      yard(0.16, 0.7, () => {
        block(0.075, 0.59, 0, 0.19, 0.25, 0.14, timber);
        m.poly([at(0.08, 0.595, 0.145), at(0.26, 0.595, 0.145), at(0.26, 0.835, 0.145), at(0.08, 0.835, 0.145)], 0x364a53);
        crystal(0.14, 0.695, 0.14, 0.064, 0.09, 0x74868b);
        crystal(0.21, 0.77, 0.14, 0.042, 0.06, 0x8e8067);
      });
      yard(0.5, 0.82, () => {
        block(0.335, 0.685, 0.075, 0.3, 0.235, 0.12, 0x566a73);
        crystal(0.465, 0.79, 0.196, 0.12, 0.09, 0x7b8d8f);
        crystal(0.555, 0.845, 0.197, 0.05, 0.05, 0xa59578);
        for (const y of [0.68, 0.895]) block(0.325, y, 0.19, 0.32, 0.027, 0.027, iron);
        for (const x of [0.325, 0.62]) block(x, 0.69, 0.19, 0.026, 0.205, 0.027, iron);
        for (const x of [0.385, 0.585]) townWheel(m, lx * x, ly * 0.93, s * 0.067, s * 0.055, 0x384548);
      });
      return frame;
    }
    case 'charcoal_kiln': {
      const b = shed(0.14, 0.1, 0.29, 0.31, 0.65);
      yard(0.58, 0.56, () => {
        const base: Vec3[] = [], crown: Vec3[] = [];
        for (let i = 0; i < 10; i++) {
          const a = i * Math.PI / 5;
          base.push(at(0.57 + Math.cos(a) * 0.29, 0.56 + Math.sin(a) * 0.29, 0.018));
          crown.push(at(0.57 + Math.cos(a) * 0.105, 0.56 + Math.sin(a) * 0.105, 0.23));
        }
        m.sorted(() => {
          for (let i = 0; i < base.length; i++) m.poly([base[i], base[(i + 1) % 10], crown[(i + 1) % 10], crown[i]], shade(0x45433a, 0.72 + (i % 5) * 0.1));
          m.poly(crown, 0x5c5844);
        });
        block(0.54, 0.53, 0.23, 0.06, 0.06, 0.065, 0x75624d);
        block(0.543, 0.533, 0.296, 0.054, 0.054, 0.006, 0x252b28);
        m.poly([at(0.5, 0.813, 0.02), at(0.64, 0.813, 0.02), at(0.617, 0.76, 0.11), at(0.524, 0.76, 0.11)], 0x222825);
        townFlame(m, lx * 0.57, ly * 0.804, s * 0.02, s * 0.038);
      });
      yard(0.24, 0.77, () => {
        for (let i = 0; i < 4; i++) block(0.12, 0.66 + i * 0.042, 0, 0.19 - i % 2 * 0.035, 0.035, 0.035, timber);
      });
      return b;
    }
    case 'steelworks': {
      stack(0.19, 0.18, 0.71, 0.115); stack(0.45, 0.14, 0.63, 0.115);
      const b = shed(0.19, 0.36, 0.62, 0.39, 0.82);
      m.within(b, () => {
        const furnace = block(0.3, 0.742, 0, 0.23, 0.055, 0.21, stone);
        townOpening(m, furnace, { ...k, opening: 'arch' }, furnace.w * 0.14, 0.8, furnace.w * 0.72, furnace.h * 0.8);
        townFlame(m, lx * 0.415, ly * 0.8, s * 0.014, s * 0.092);
      });
      return b;
    }
    case 'toolsmith': {
      const b = shed(0.13, 0.15, 0.55, 0.39, 0.8);
      rack(0.19, 0.76, false);
      yard(0.73, 0.71, () => {
        block(0.62, 0.65, 0, 0.22, 0.18, 0.08, timber);
        block(0.655, 0.68, 0.08, 0.15, 0.11, 0.04, iron);
        block(0.7, 0.695, 0.12, 0.055, 0.08, 0.08, iron);
        block(0.63, 0.67, 0.2, 0.22, 0.125, 0.045, 0xa3b3b8);
        m.poly([at(0.63, 0.67, 0.245), at(0.52, 0.73, 0.23), at(0.63, 0.795, 0.245)], 0xc1cbca);
        m.poly([at(0.52, 0.73, 0.23), at(0.63, 0.795, 0.2), at(0.63, 0.795, 0.245)], iron);
      });
      return b;
    }
    case 'frame_workshop': case 'runic_joinery': {
      const arcane = id === 'runic_joinery';
      const b = shed(0.12, 0.1, 0.3, 0.56, 0.79);
      const frameColor = arcane ? 0x807697 : timber;
      yard(0.65, 0.59, () => {
        block(0.48, 0.46, 0, 0.37, 0.3, 0.035, frameColor);
        for (const x of [0.48, 0.8]) {
          block(x, 0.48, 0.035, 0.043, 0.04, 0.39, frameColor);
          beam([x, 0.73, 0.025], [x + 0.02, 0.51, 0.36], frameColor, 2.3);
        }
        block(0.46, 0.475, 0.425, 0.405, 0.057, 0.045, arcane ? 0xb2a0d0 : timber);
        if (arcane) {
          crystal(0.665, 0.6, 0.11, 0.078, 0.22, 0xac8ada);
          for (const x of [0.5, 0.82]) for (const z of [0.15, 0.28, 0.4]) beam([x - 0.012, 0.523, z], [x + 0.012, 0.523, z + 0.018], 0xc9b4f0, 1.25);
          ring(0.665, 0.65, 0.23, 0.105, 0x9e89c1);
        } else {
          beam([0.65, 0.505, 0.43], [0.65, 0.505, 0.24], 0x433d31, 0.75);
          block(0.54, 0.49, 0.2, 0.24, 0.15, 0.03, 0xc0a16b);
          for (const x of [0.56, 0.76]) townWheel(m, lx * x, ly * 0.79, s * 0.063, s * 0.064, iron);
          block(0.52, 0.7, 0.065, 0.3, 0.06, 0.055, timber);
        }
      });
      return b;
    }
    case 'fitting_foundry': {
      const b = shed(0.15, 0.13, 0.57, 0.4, 0.8);
      yard(0.6, 0.76, () => {
        block(0.35, 0.65, 0, 0.49, 0.21, 0.065, 0x665648);
        for (const [x, radius, tint] of [[0.5, 0.155, iron], [0.745, 0.098, brass]]) {
          ring(x, 0.745, radius + 0.063, radius, tint, true);
          beam([x, 0.747, 0.065], [x, 0.747, radius * 2 + 0.06], tint, 1.7);
          beam([x - radius * s / lx, 0.747, radius + 0.063], [x + radius * s / lx, 0.747, radius + 0.063], tint, 1.7);
        }
      });
      return b;
    }
    case 'armament_workshop': {
      const b = house(lx * 0.15, ly * 0.13, lx * 0.64, ly * 0.41, h * 0.9, true, 2);
      rack(0.18, 0.72, true); rack(0.57, 0.72, true);
      yard(0.49, 0.84, () => {
        for (let i = 0; i < 3; i++) {
          const x = 0.31 + i * 0.17, y = 0.815;
          m.poly([at(x, y, 0.18), at(x + 0.09, y, 0.18), at(x + 0.09, y, 0.085), at(x + 0.045, y, 0.045), at(x, y, 0.085)], i % 2 ? iron : mix(k.trim, 0xa98455, 0.45));
          beam([x + 0.045, y + 0.001, 0.06], [x + 0.045, y + 0.001, 0.167], 0xc9c7ad, 1);
        }
      });
      return b;
    }
    case 'lens_workshop': {
      const b = shed(0.22, 0.25, 0.54, 0.5, 0.88);
      m.within(b, () => {
        const z = b.peak / s;
        block(0.43, 0.44, z - 0.06, 0.15, 0.15, 0.1, stone);
        beam([0.47, 0.51, z + 0.02], [0.4, 0.51, z + 0.19], brass, 1.7);
        beam([0.55, 0.51, z + 0.02], [0.62, 0.51, z + 0.19], brass, 1.7);
        ring(0.51, 0.51, z + 0.2, 0.17, 0xd0b079);
        m.poly([at(0.51, 0.51, z + 0.095), at(0.405, 0.51, z + 0.2), at(0.51, 0.51, z + 0.305), at(0.615, 0.51, z + 0.2)], 0xa1d0d0, 0, undefined, 0.3);
      });
      return b;
    }
    case 'core_foundry': case 'heart_assembly': {
      const heart = id === 'heart_assembly', metal = heart ? 0xa4b4bc : brass;
      const b = shed(0.13, 0.12, 0.28, 0.56, 0.9);
      yard(0.65, 0.57, () => {
        block(0.45, 0.36, 0, 0.37, 0.38, 0.075, stone);
        block(0.49, 0.4, 0.075, 0.29, 0.3, 0.025, iron);
        const height = heart ? 0.63 : 0.47;
        for (const x of [0.47, 0.78]) block(x, 0.38, 0.09, 0.025, 0.025, height - 0.11, metal);
        crystal(0.64, 0.55, 0.15, heart ? 0.112 : 0.13, heart ? 0.31 : 0.25, heart ? 0x91d6d0 : 0xf2c379);
        // Foreground ribs follow the core; the luminous facets remain visible.
        for (const x of [0.47, 0.78]) {
          block(x, 0.68, 0.09, 0.025, 0.025, height - 0.11, metal);
          beam([x + 0.012, 0.693, height - 0.025], [0.635, 0.55, height + 0.07], metal, 1.7);
          beam([x + 0.012, 0.393, height - 0.025], [0.635, 0.55, height + 0.07], metal, 1.7);
        }
        block(0.445, 0.365, height - 0.035, 0.38, 0.045, 0.04, metal);
        block(0.445, 0.67, height - 0.035, 0.38, 0.045, 0.04, metal);
        if (heart) ring(0.64, 0.7, 0.31, 0.14, 0x85949f);
        else townWheel(m, lx * 0.81, ly * 0.58, s * 0.18, s * 0.078, brass, true);
      });
      return b;
    }
    default: return null;
  }
}

export function drawLowPolyBuilding(g: Graphics, def: BuildingType, skin: Vernacular, W: number, H: number, pad: number): LowPolyBuildingGeometry {
  const shape = def.vis.shape;
  // Domestic thatch and darker hall/workshop shingles distinguish roof masses
  // at city zoom without changing a faction's masonry or ownership colours.
  const thatched = skin.id === 'ashen' && ['hut', 'farm', 'windmill', 'camp'].includes(shape);
  const k: Vernacular = skin.id === 'ashen' ? {
    ...skin,
    roof: thatched ? ASHEN_MATERIALS.roof_0 : ASHEN_MATERIALS.roof_dark,
    roofLit: thatched ? ASHEN_MATERIALS.roof_2 : ASHEN_MATERIALS.roof_1,
  } : skin;
  const ratio = Math.max(W, H);
  const lx = 100 * W / ratio, ly = 100 * H / ratio;
  const size = Math.min(lx, ly);
  const m = new TownMesh(lx, ly);
  const wall = k.id === 'ashen' ? ASHEN_MATERIALS.plaster : mix(shade(k.wallLit, 1.72), 0xc7b991, 0.26);
  const timber = k.id === 'ashen' ? ASHEN_MATERIALS.timber : mix(shade(k.timber, 1.6), 0xa47b51, 0.2);
  const masonry = k.id === 'ashen' ? ASHEN_MATERIALS.stone_1 : wall;
  const footing = k.id === 'ashen' ? ASHEN_MATERIALS.stone_1 : mix(k.wall, 0xa39575, 0.43);
  const h = size * (0.25 + k.stance * 0.045) * (1 - k.sunken * 0.35);
  const rise = size * 0.184;
  // Thin, irregular earth blends into the terrain; no upright square pedestal.
  m.ground([[lx * 0.12, ly * 0.05], [lx * 0.8, ly * 0.07], [lx * 0.96, ly * 0.3], [lx * 0.91, ly * 0.83], [lx * 0.68, ly * 0.96], [lx * 0.16, ly * 0.91], [lx * 0.05, ly * 0.6]], 0x7c7355, 0.24);
  const house = (x = lx * 0.2, y = ly * 0.17, w = lx * 0.58, d = ly * 0.61, height = h, civic = false, doors = 1): TownBlock => {
    return m.assembly(x + y + (w + d) / 2, () => {
      m.box(x - 1, y - 1, 0, w + 2, d + 2, 1.6, footing);
      const b = m.box(x, y, 1.6, w, d, height, wall, k.batter * height * 0.15, false);
      const framed = ['rubble', 'daub', 'log', 'plank'].includes(k.fabric);
      if (framed) {
        const infill = k.id === 'ashen' ? ASHEN_MATERIALS.plaster_light : mix(wall, 0xd0bb8f, 0.28);
        m.poly([[x, y + d, b.z + height * 0.28], [x + w, y + d, b.z + height * 0.28],
          [x + w, y + d, b.peak], [x, y + d, b.peak]], infill, 0);
        m.poly([[x + w, y, b.z + height * 0.28], [x + w, y + d, b.z + height * 0.28],
          [x + w, y + d, b.peak], [x + w, y, b.peak]], shade(infill, 0.76), 0);
      }
      townFront(m, b, k, doors, true, framed);
      // Fortified towers keep their battlements; inhabited halls have a roof.
      const hallSkin = civic && k.crown === 'battlement' ? { ...k, crown: 'gable' as const } : k;
      townRoof(m, b, hallSkin, rise * Math.min(1, w / (lx * 0.52)), civic);
      return b;
    });
  };
  const fence = (x0: number, y0: number, x1: number, y1: number) => {
    const length = Math.hypot(x1 - x0, y1 - y0);
    const n = Math.max(2, Math.round(length / (size * 0.12)));
    for (let i = 0; i <= n; i++) m.box(x0 + (x1 - x0) * i / n, y0 + (y1 - y0) * i / n, 0, 1.3, 1.3, size * 0.09, timber);
    for (const z of [size * 0.035, size * 0.075]) m.stroke([x0, y0, z], [x1, y1, z], timber, 1);
  };
  const industry = townIndustry(m, k, def.id, lx, ly, h, house);
  let b: TownBlock;
  if (industry) b = industry;
  else switch (shape) {
    case 'hearth': {
      const stature = 0.72 + def.vis.height * 0.25;
      house(lx * 0.12, ly * 0.36, lx * 0.25, ly * 0.36, h * 0.83 * stature, false);
      b = house(lx * 0.32, ly * 0.16, lx * 0.47, ly * 0.61, h * 1.32 * stature, true);
      house(lx * 0.63, ly * 0.63, lx * 0.22, ly * 0.23, h * 0.61, false);
      townPorch(m, b, k, 0.61, 0.23);
      break;
    }
    case 'tower': case 'belfry': case 'shrine': {
      const width = lx * (shape === 'shrine' ? 0.39 : 0.33);
      const depth = ly * 0.38;
      b = m.assembly((lx + ly) / 2, () => {
        const stature = 0.48 + def.vis.height * 0.35;
        m.box((lx - width) / 2 - 0.7, (ly - depth) / 2 - 0.7, 0, width + 1.4, depth + 1.4, 1.6, footing);
        // A narrow rectangular footprint must not shorten a tall authored tower.
        const shaft = m.box((lx - width) / 2, (ly - depth) / 2, 1.6, width, depth, 66 * (0.8 + k.stance * 0.15) * stature, masonry, k.batter * 3, false);
        townFront(m, shaft, k, 1, false);
        townOpening(m, shaft, k, width * 0.42, shaft.h * 0.61, width * 0.12, shaft.h * 0.15, def.vis.glow > 0);
        townOpening(m, shaft, k, depth * 0.43, shaft.h * 0.65, depth * 0.11, shaft.h * 0.13, false, true);
        const cap = m.box(shaft.x - width * 0.08, shaft.y - depth * 0.08, shaft.peak, width * 1.16, depth * 1.16, shape === 'belfry' ? h * 0.45 : 2, masonry, 0, false);
        if (shape === 'belfry') townOpening(m, cap, { ...k, opening: 'arch' }, cap.w * 0.25, cap.z + 1, cap.w * 0.5, cap.h * 0.8);
        townRoof(m, cap, k, rise * 0.8, true);
        shaft.peak = cap.peak;
        if (shape === 'shrine') {
          const cx = shaft.x + shaft.w / 2, cy = shaft.y + shaft.d / 2;
          m.stroke([cx, cy, shaft.peak], [cx, cy, shaft.peak + size * 0.1], 0xc9ac70, 1.2);
          m.stroke([cx - size * 0.035, cy, shaft.peak + size * 0.065], [cx + size * 0.035, cy, shaft.peak + size * 0.065], 0xc9ac70, 1.2);
        }
        return shaft;
      });
      break;
    }
    case 'farm': case 'field': {
      b = m.box(lx * 0.12, ly * 0.15, 0, lx * 0.76, ly * 0.73, 1.1, 0x68583e);
      for (let i = 0; i < 9; i++) {
        const x = lx * (0.15 + i * 0.079);
        m.poly([[x, ly * 0.19, 1.2], [x + lx * 0.035, ly * 0.19, 1.2], [x + lx * 0.035, ly * 0.84, 1.2], [x, ly * 0.84, 1.2]], i % 2 ? 0xa6a45c : 0x84914c, 0);
      }
      if (shape === 'farm') b = house(lx * 0.13, ly * 0.12, lx * 0.29, ly * 0.28, h * 0.66);
      fence(lx * 0.09, ly * 0.13, lx * 0.09, ly * 0.9);
      fence(lx * 0.09, ly * 0.9, lx * 0.89, ly * 0.9);
      break;
    }
    case 'mine': case 'pit': {
      const p: Vec3 = [lx * 0.43, ly * 0.41, size * 0.3];
      const ring: Vec3[] = [[lx * 0.1, ly * 0.24, 0], [lx * 0.54, ly * 0.09, 0], [lx * 0.87, ly * 0.35, 0], [lx * 0.86, ly * 0.8, 0], [lx * 0.18, ly * 0.8, 0]];
      for (let i = 0; i < ring.length; i++) m.poly([ring[i], ring[(i + 1) % ring.length], p], shade(masonry, 0.74 + i * 0.1));
      b = m.box(lx * 0.34, ly * 0.68, 0, lx * 0.3, ly * 0.08, h * 0.85, timber);
      townOpening(m, b, { ...k, opening: 'arch' }, b.w * 0.12, 0, b.w * 0.76, b.h * 0.85);
      if (shape === 'mine') for (const x of [lx * 0.42, lx * 0.55]) m.stroke([x, ly * 0.77, 0.7], [x, ly * 0.99, 0.7], 0x93988a, 1);
      break;
    }
    case 'market': {
      b = { x: lx * 0.2, y: ly * 0.2, z: 0, w: lx * 0.6, d: ly * 0.6, h: h * 0.5, peak: h * 0.5 };
      for (const [fx, fy] of [[0.14, 0.14], [0.57, 0.14], [0.14, 0.6], [0.57, 0.6]]) {
        const x = lx * fx, y = ly * fy, w = lx * 0.27, d = ly * 0.26;
        m.assembly(x + y + (w + d) / 2, () => {
          const back = h * 0.88, front = h * 0.73, canopyFront = y + d * 0.69;
          const cloth = fx < 0.5 ? 0x718e71 : 0xb47952;
          const cream = 0xdac9a0;
          // A narrow serving counter projects beyond the open cloth canopy.
          const counter = m.box(x, y + d * 0.64, 0, w, d * 0.36, h * 0.25, timber);
          m.box(x - 0.7, counter.y - 0.4, counter.peak, w + 1.4, counter.d + 0.8, 1.1, 0xb69461);
          for (const t of [0.2, 0.4, 0.6, 0.8]) {
            m.stroke([x + w * t, y + d + 0.04, 0.8], [x + w * t, y + d + 0.04, counter.peak], shade(timber, 0.64), 0.6);
          }
          const goods = fx < 0.5 ? [0x9e4939, 0x8d9f4d, 0xc9a24e] : [0xd2b17c, 0xb67c43, 0x8f6448];
          for (let i = 0; i < 3; i++) {
            const gx = x + w * (0.18 + i * 0.32), gy = y + d * 0.83;
            const r = w * 0.12, gz = counter.peak + 1.1, top = gz + h * (0.08 + (i % 2) * 0.025);
            m.box(gx - r - 0.3, gy - d * 0.095, gz, r * 2 + 0.6, d * 0.19, 0.8, 0x725638);
            const peak: Vec3 = [gx - r * 0.18, gy, top];
            const a: Vec3 = [gx - r, gy - d * 0.075, gz + 0.8], c: Vec3 = [gx + r, gy - d * 0.075, gz + 0.8];
            const e: Vec3 = [gx + r, gy + d * 0.075, gz + 0.8], f: Vec3 = [gx - r, gy + d * 0.075, gz + 0.8];
            m.poly([a, c, peak], shade(goods[i], 1.14), 0.2);
            m.poly([c, e, peak], shade(goods[i], 0.76), 0.2);
            m.poly([e, f, a, peak], goods[i], 0.2);
          }
          const crate = m.box(x + w * 0.69, y + d * 0.18, 0, w * 0.28, d * 0.25, h * 0.27, 0xa78351);
          m.stroke([crate.x, crate.y + crate.d, 1], [crate.x + crate.w, crate.y + crate.d, crate.peak - 0.4], shade(timber, 0.78), 1);
          for (const px of [x, x + w - 1.2]) {
            m.box(px, y, 0, 1.2, 1.2, back, timber);
            m.box(px, canopyFront - 1.2, 0, 1.2, 1.2, front, timber);
          }
          // Striped fabric has a shallow sag and a short hanging front hem;
          // no closed gable or masonry appears above the stocked counter.
          const roofPoint = (u: number, t: number): Vec3 => [x - 1.4 + (w + 2.8) * u, y - 1 + (canopyFront - y + 2) * t,
            back + (front - back) * t - Math.sin(t * Math.PI) * h * 0.035];
          for (let strip = 0; strip < 5; strip++) {
            const u0 = strip / 5, u1 = (strip + 1) / 5, color = strip % 2 ? cream : cloth;
            for (const [t0, t1] of [[0, 0.52], [0.52, 1]]) {
              m.poly([roofPoint(u0, t0), roofPoint(u1, t0), roofPoint(u1, t1), roofPoint(u0, t1)], shade(color, t0 === 0 ? 1.08 : 0.94), 0.25);
            }
            const left = roofPoint(u0, 1), right = roofPoint(u1, 1);
            m.poly([left, right, [right[0], right[1], right[2] - 1.7], [left[0], left[1], left[2] - 1.7]], shade(color, 0.8), 0.2);
          }
          m.stroke([x, canopyFront, front - 1.8], [x + w, canopyFront, front - 1.8], timber, 1.2);
        });
      }
      break;
    }
    case 'yard': case 'range': case 'barracks': case 'stable': case 'camp': {
      b = house(lx * 0.16, ly * 0.13, lx * 0.67, ly * 0.43, h * 0.95, shape === 'barracks', shape === 'stable' ? 3 : 1);
      townPorch(m, b, k, shape === 'stable' ? 0.88 : 0.56, 0.26);
      fence(lx * 0.11, ly * 0.12, lx * 0.11, ly * 0.87);
      fence(lx * 0.11, ly * 0.87, lx * 0.4, ly * 0.87);
      fence(lx * 0.62, ly * 0.87, lx * 0.88, ly * 0.87);
      fence(lx * 0.88, ly * 0.12, lx * 0.88, ly * 0.87);
      for (let i = 0; i < 3; i++) {
        const x = lx * (0.3 + i * 0.2), y = ly * 0.71;
        if (shape === 'range') {
          m.stroke([x, y, 0], [x, y, size * 0.12], timber, 1.3);
          townWheel(m, x, y, size * 0.12, size * 0.055, 0xd3c398);
          townWheel(m, x, y + 0.05, size * 0.12, size * 0.022, 0x9e5c43);
        } else m.box(x, y, 0, lx * 0.075, ly * 0.08, size * 0.065, timber);
      }
      break;
    }
    case 'forge': case 'oven': case 'sawmill': {
      b = house(lx * 0.16, ly * 0.19, lx * 0.6, ly * 0.53, h, false);
      const stack = m.box(lx * 0.68, ly * 0.2, 0, lx * 0.12, ly * 0.15, h + rise * 1.3, k.id === 'ashen' ? masonry : mix(wall, 0x977457, 0.22));
      m.box(stack.x - 0.6, stack.y - 0.6, stack.peak, stack.w + 1.2, stack.d + 1.2, 1.5, shade(masonry, 0.83));
      townFlame(m, lx * 0.5, ly * 0.725, 1.5, size * 0.065, facadeDepth(b) + 0.3);
      if (shape === 'sawmill') {
        for (let i = 0; i < 3; i++) m.box(lx * 0.47, ly * (0.78 + i * 0.047), 0, lx * 0.32, ly * 0.035, 3, timber);
        townWheel(m, lx * 0.72, ly * 0.74, h * 0.42, size * 0.08, 0x9da69b, true, facadeDepth(b) + 0.4);
      }
      break;
    }
    case 'windmill': {
      b = house(lx * 0.34, ly * 0.3, lx * 0.34, ly * 0.36, h * 1.65);
      m.within(b, () => {
        const x = b.x + b.w / 2, y = b.y + b.d + 3;
        const z = b.z + b.h + Math.min(size * 0.055, (b.peak - b.z - b.h) * 0.26);
        const radius = size * 0.28, sail = 0xdfcda6;
        // The axle joins the gable; broad cloth sails sit in its front plane.
        m.box(x - 1.7, b.y + b.d - 0.4, z - 1.7, 3.4, 3.6, 3.4, shade(timber, 0.75));
        for (let i = 0; i < 4; i++) {
          const a = i * Math.PI / 2 + 0.35, ca = Math.cos(a), sa = Math.sin(a);
          const pt = (r: number, width: number): Vec3 => [x + ca * r - sa * width, y, z + sa * r + ca * width];
          const inner = radius * 0.3, outer = radius, narrow = size * 0.025, broad = size * 0.092;
          m.poly([pt(inner, 0), pt(outer, 0), pt(outer, broad), pt(inner, narrow)], i % 2 ? shade(sail, 0.9) : sail, 0.55);
          m.poly([pt(inner, narrow * 0.65), pt(outer, broad * 0.72), pt(outer, broad), pt(inner, narrow)], shade(sail, 0.78), 0);
          m.stroke([x, y, z], pt(radius + 0.8, 0), timber, 1.7);
          m.stroke(pt(inner, narrow), pt(outer, broad), shade(timber, 1.15), 0.9);
          for (const t of [0.35, 0.66, 0.91]) {
            const r = inner + (outer - inner) * t;
            m.stroke(pt(r, 0), pt(r, narrow + (broad - narrow) * t), shade(timber, 1.12), 0.7);
          }
        }
        townWheel(m, x, y + 0.05, z, size * 0.034, shade(timber, 0.72));
        townWheel(m, x, y + 0.08, z, size * 0.014, 0xbda67d);
      });
      break;
    }
    case 'granary': {
      b = house(lx * 0.23, ly * 0.23, lx * 0.54, ly * 0.53, h * 1.12);
      for (const t of [0.26, 0.68]) {
        m.stroke([b.x, b.y + b.d + 0.04, b.z + b.h * t], [b.x + b.w, b.y + b.d + 0.04, b.z + b.h * t], timber, 1.1, facadeDepth(b) + 0.15);
        m.stroke([b.x + b.w + 0.04, b.y, b.z + b.h * t], [b.x + b.w + 0.04, b.y + b.d, b.z + b.h * t], timber, 1.1, facadeDepth(b, true) + 0.15);
      }
      break;
    }
    case 'well': case 'brazier': {
      b = m.assembly((lx + ly) / 2, () => {
        const basin = m.box(lx * 0.34, ly * 0.34, 0, lx * 0.32, ly * 0.32, h * 0.4, masonry);
        m.poly([[basin.x + 2, basin.y + 2, basin.peak + 0.1], [basin.x + basin.w - 2, basin.y + 2, basin.peak + 0.1], [basin.x + basin.w - 2, basin.y + basin.d - 2, basin.peak + 0.1], [basin.x + 2, basin.y + basin.d - 2, basin.peak + 0.1]], shape === 'well' ? 0x496e70 : 0x4d3d2d);
        if (shape === 'brazier') townFlame(m, lx * 0.5, ly * 0.5, basin.peak + 0.2, size * 0.12);
        else {
          for (const x of [basin.x + 1, basin.x + basin.w - 2]) m.box(x, basin.y + basin.d * 0.46, basin.peak, 1.5, 1.5, h * 0.7, timber);
          const cap = { ...basin, z: basin.peak, h: h * 0.7 };
          townRoof(m, cap, k, rise * 0.6, false);
          basin.peak = cap.peak;
        }
        return basin;
      });
      break;
    }
    case 'gibbet':
      b = m.box(lx * 0.34, ly * 0.4, 0, 2.8, 2.8, size * 0.47, timber);
      m.stroke([b.x, b.y, b.peak], [lx * 0.74, b.y, b.peak], timber, 2.7);
      m.stroke([b.x, b.y, b.peak * 0.67], [lx * 0.58, b.y, b.peak], timber, 1.5);
      m.stroke([lx * 0.7, b.y, b.peak], [lx * 0.7, b.y, b.peak * 0.58], 0xae9d7b, 0.8);
      townWheel(m, lx * 0.7, b.y, b.peak * 0.53, size * 0.027, 0xae9d7b);
      break;
    case 'hut':
      b = house(lx * 0.25, ly * 0.16, lx * 0.48, ly * 0.6, h * 0.92);
      townPorch(m, b, k, 0.58, 0.24);
      break;
    case 'store':
      b = house(lx * 0.18, ly * 0.17, lx * 0.63, ly * 0.58, h * 0.94, false, 2);
      townPorch(m, b, k, 0.82, 0.25);
      break;
    default:
      b = house(lx * 0.2, ly * 0.16, lx * 0.6, ly * 0.61, h);
      townPorch(m, b, k);
      break;
  }
  if (!industry) m.within(b, () => townMotif(m, k, def.vis.motif, b));
  if (def.vis.banner) {
    const x = lx * 0.15, y = ly * 0.77, height = size * 0.3;
    const color = mix(k.trim, 0x9a6351, 0.55);
    m.stroke([x, y, 0], [x, y, height], timber, 1);
    m.poly([[x, y, height], [x + size * 0.11, y, height - size * 0.022], [x + size * 0.08, y, height - size * 0.07], [x, y, height - size * 0.085]], color);
  }
  return m.draw(g, W, H, pad);
}

interface MeshFace { points: number[]; color: number; depth: number }

/** Eight view directions share the same mesh; the miniature stays upright as it turns. */
class Miniature {
  private readonly faces: MeshFace[] = [];
  constructor(private readonly angle: number, private readonly scale: number) {}
  project(v: Vec3): Point {
    const c = Math.cos(this.angle), s = Math.sin(this.angle);
    return [(v[0] * c - v[1] * s) * this.scale, ((v[0] * s + v[1] * c) * 0.5 - v[2] * 0.92) * this.scale];
  }
  poly(vertices: Vec3[], color: number): void {
    const points = vertices.flatMap((v) => this.project(v));
    const depth = vertices.reduce((sum, v) => sum + v[0] * Math.sin(this.angle) + v[1] * Math.cos(this.angle) + v[2] * 0.15, 0) / vertices.length;
    this.faces.push({ points, color, depth });
  }
  box(x: number, y: number, z: number, w: number, d: number, h: number, color: number, taper = 0): void {
    const low: Vec3[] = [[x - w / 2, y - d / 2, z], [x + w / 2, y - d / 2, z], [x + w / 2, y + d / 2, z], [x - w / 2, y + d / 2, z]];
    const top = low.map(([vx, vy]) => [vx + Math.sign(x - vx) * taper, vy + Math.sign(y - vy) * taper, z + h] as Vec3);
    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4;
      const a = low[i], b = low[j];
      const normalX = b[1] - a[1], normalY = a[0] - b[0];
      const view = normalX * Math.sin(this.angle) + normalY * Math.cos(this.angle);
      if (view <= 0) continue;
      const light = 0.82 + 0.24 * Math.max(0, -(normalX * Math.cos(this.angle) - normalY * Math.sin(this.angle)) / Math.max(w, d));
      this.poly([a, b, top[j], top[i]], shade(color, light));
    }
    this.poly(top, shade(color, 1.3));
  }
  crystal(x: number, y: number, z: number, r: number, h: number, color: number): void {
    const ring: Vec3[] = [];
    for (let i = 0; i < 6; i++) ring.push([x + Math.cos(i * Math.PI / 3) * r, y + Math.sin(i * Math.PI / 3) * r, z]);
    for (let i = 0; i < 6; i++) this.poly([ring[i], ring[(i + 1) % 6], [x, y, z + h]], shade(color, 0.7 + (i / 5) * 0.65));
  }
  /** Jointed, tapered limbs have an actual elbow/knee instead of stacked blocks. */
  limb(a: Vec3, b: Vec3, r0: number, r1: number, color: number, sides = 6): void {
    const d = b.map((v, i) => v - a[i]);
    const length = Math.hypot(...d) || 1;
    const n = d.map(v => v / length);
    const cross = (u: number[], v: number[]): Vec3 => [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
    const raw = cross(n, Math.abs(n[2]) < 0.9 ? [0, 0, 1] : [0, 1, 0]);
    const norm = Math.hypot(...raw);
    const u = raw.map(v => v / norm), v = cross(n, u);
    const ring = (p: Vec3, r: number): Vec3[] => Array.from({ length: sides }, (_, i) => {
      const angle = i / sides * Math.PI * 2;
      return p.map((value, j) => value + r * (u[j] * Math.cos(angle) + v[j] * Math.sin(angle))) as Vec3;
    });
    const low = ring(a, r0), high = ring(b, r1);
    for (let i = 0; i < sides; i++) {
      const j = (i + 1) % sides, angle = (i + 0.5) / sides * Math.PI * 2;
      const nx = u[0] * Math.cos(angle) + v[0] * Math.sin(angle);
      const ny = u[1] * Math.cos(angle) + v[1] * Math.sin(angle);
      const nz = u[2] * Math.cos(angle) + v[2] * Math.sin(angle);
      const screenX = nx * Math.cos(this.angle) - ny * Math.sin(this.angle);
      const depth = nx * Math.sin(this.angle) + ny * Math.cos(this.angle);
      this.poly([low[i], low[j], high[j], high[i]], shade(color, 0.78 + Math.max(0, -screenX * 0.22 - depth * 0.13 + nz * 0.3)));
    }
    this.poly(high, shade(color, 1.12));
  }
  rounded(x: number, y: number, z: number, w: number, d: number, h: number, color: number): void {
    const rings = [0, 0.2, 0.72, 1].map((height, row) => {
      const radius = [0.48, 0.96, 1, 0.58][row];
      return Array.from({ length: 8 }, (_, i): Vec3 => [x + Math.cos(i * Math.PI / 4) * w * radius / 2,
        y + Math.sin(i * Math.PI / 4) * d * radius / 2, z + height * h]);
    });
    for (let r = 0; r < rings.length - 1; r++) for (let i = 0; i < 8; i++) {
      const j = (i + 1) % 8;
      const angle = (i + 0.5) * Math.PI / 4 + this.angle;
      this.poly([rings[r][i], rings[r][j], rings[r + 1][j], rings[r + 1][i]],
        shade(color, 0.79 + Math.max(0, -Math.cos(angle) * 0.2 - Math.sin(angle) * 0.13) + r * 0.045));
    }
    this.poly(rings[3], shade(color, 1.16));
  }
  draw(g: Graphics): void {
    this.faces.sort((a, b) => a.depth - b.depth);
    for (const f of this.faces) face(g, f.points, f.color, 0.08);
  }
}

function miniatureScale(vis: UnitVisual): number {
  const scale = vis.scale <= 1.15 ? vis.scale : 1.15 + (vis.scale - 1.15) * 0.55;
  // Smaller heads and natural feet removed 6% of a person's standing height.
  // Restore the original crown-to-ground scale against the same city doors.
  return scale * (['villager', 'infantry', 'archer', 'caster'].includes(vis.build) ? 1.06 : 1);
}

function miniatureHand(vis: UnitVisual, frame: number, pose: UnitPose): Vec3 {
  const movement = gait(frame, pose);
  const breadth = vis.build === 'brute' ? 13 : vis.build === 'villager' ? 7.3 : 8.7 + vis.pauldron * 0.7;
  const baseZ = vis.build === 'cavalry' ? 8 : 0;
  return [pose === 'work' ? 5 + movement.bend * 4 : 3.5 - movement.stride * 1.8,
    pose === 'work' ? 1.2 : breadth * 0.56 + 0.6, baseZ + (pose === 'work' ? 20 - movement.bend * 11 : 12.5)];
}

export function lowPolyHand(vis: UnitVisual, direction: number, frame = 0, pose: UnitPose = 'idle'): { x: number; y: number } {
  const s = miniatureScale(vis);
  const m = new Miniature(direction * Math.PI / 4, s);
  const [x, y] = m.project(miniatureHand(vis, frame, pose));
  return { x, y };
}

export function drawLowPolyUnit(g: Graphics, vis: UnitVisual, p: MiniPalette, frame: number, direction: number, pose: UnitPose = 'walk'): void {
  const s = miniatureScale(vis);
  const m = new Miniature(direction * Math.PI / 4, s);
  const steel = mix(p.metal, 0x99a3a2, 0.18);
  const cloth = mix(p.cloth, p.accent, 0.16);
  const leather = 0x584330;
  const skin = vis.build === 'brute' ? mix(p.accent, 0xafa178, 0.6) : 0xc5a582;
  const movement = gait(frame, pose);
  const step = movement.stride;
  const humanoid = (z: number, big = false) => {
    const breadth = big ? 13 : vis.build === 'villager' ? 7.3 : 8.7 + vis.pauldron * 0.7;
    const bend = movement.bend;
    const lean = bend * 3;
    const shoulderZ = z + 19.2 - bend * 2;
    for (const side of [-1, 1]) {
      const forward = side * step * (vis.build === 'cavalry' ? 0.6 : 3.5);
      const lift = side < 0 ? movement.liftLeft : movement.liftRight;
      const ankle: Vec3 = [forward, side * breadth * 0.24, z + 1.4 + lift];
      const knee: Vec3 = [forward * 0.42 + lift * 0.65, side * breadth * 0.23, z + 6.3 + lift * 0.25];
      const hip: Vec3 = [0, side * breadth * 0.2, z + 11.6];
      m.rounded(forward + 0.8, ankle[1], z + lift, 4.8, 2.6, 2.4, leather);
      m.limb(ankle, knee, 1.2, 1.35, shade(cloth, 0.72));
      m.limb(knee, hip, 1.4, 1.7, shade(cloth, 0.85));
    }
    if (vis.cloak || vis.build === 'caster' || vis.build === 'champion') {
      const trail = -6.5 - Math.abs(step) * 1.1;
      m.poly([[-3.5, -breadth * 0.48, shoulderZ + 0.6], [-3.5, breadth * 0.48, shoulderZ + 0.6],
        [trail, breadth * 0.65, z + 4], [trail - 1, 0, z + 2.5], [trail, -breadth * 0.65, z + 4]], shade(cloth, 0.72));
      m.poly([[-3.5, 0, shoulderZ + 0.6], [trail - 1, 0, z + 2.5], [trail, -breadth * 0.65, z + 4],
        [-3.5, -breadth * 0.48, shoulderZ + 0.6]], shade(cloth, 1.08));
    }
    m.rounded(lean * 0.55, 0, z + 10, big ? 9 : 6.2, breadth, 10.7 - bend * 1.5,
      vis.build === 'villager' || vis.build === 'caster' || vis.build === 'archer' ? cloth : steel);
    m.rounded(lean * 0.25, 0, z + 11, big ? 9.3 : 6.5, breadth * 0.91, 1.2, leather);
    // A soft cloth skirt and a narrow fastening break up the armour plate.
    m.box(3.2 + lean * 0.4, 0, z + 10.2, 0.4, breadth * 0.42, 7, cloth);
    m.box(3.6 + lean * 0.25, 0, z + 11.2, 0.5, 1.2, 0.8, shade(p.accent, 0.95));
    for (const side of [-1, 1]) {
      const shoulder: Vec3 = [lean, side * breadth * 0.47, shoulderZ];
      const rightHand = miniatureHand(vis, frame, pose);
      const hand: Vec3 = side > 0 ? rightHand : [pose === 'work' ? rightHand[0] - 1.4 : 2.5 + step * 1.9,
        pose === 'work' ? 0.4 : -breadth * 0.56 - 0.6, pose === 'work' ? rightHand[2] - 1.6 : z + 12.4];
      const elbow: Vec3 = [(shoulder[0] + hand[0]) * 0.5 - 1.3, side * (breadth * 0.58 + 1),
        (shoulder[2] + hand[2]) * 0.5 - 0.7];
      m.limb(shoulder, elbow, big ? 2.2 : 1.55, big ? 1.8 : 1.3, cloth);
      m.limb(elbow, hand, 1.3, 0.85, vis.build === 'villager' ? skin : shade(steel, 0.85));
      m.rounded(hand[0], hand[1], hand[2] - 0.8, 2, 1.8, 1.8, skin);
      if (vis.pauldron > 0) m.rounded(lean, side * breadth * 0.49, shoulderZ - 1.5, 5, 4, 3.1, steel);
      if (vis.pauldron > 1) m.crystal(lean, side * breadth * 0.61, shoulderZ + 1.4, 1.45, 3.2, steel);
    }
    const headColor = vis.helm > 0 && vis.helm < 5 ? steel : vis.helm === 5 ? cloth : skin;
    const headZ = shoulderZ + 1.9;
    m.limb([lean, 0, shoulderZ], [lean + 0.4, 0, headZ + 1], 1.1, 1.2, skin);
    m.rounded(lean + 0.6, 0, headZ, big ? 5.8 : 4.6, big ? 5.8 : 4.7, 5.3, headColor);
    // A recessed visor or face on the forward plane makes the facing readable.
    m.box(lean + 2.85, 0, headZ + 2.7, 0.45, 3.1, 0.65, vis.helm ? 0x303436 : 0x755942);
    if (!vis.helm) {
      m.rounded(lean + 0.1, 0, headZ + 3.5, 4.8, 4.9, 1.9, 0x594332);
      m.box(lean + 3, 0, headZ + 1.7, 0.6, 0.9, 1, skin);
    }
    if (vis.helm === 1) m.rounded(lean + 0.4, 0, headZ + 2.8, 6.8, 6.8, 0.8, steel);
    if (vis.helm === 2) m.box(lean + 3.1, 0, headZ + 0.9, 0.6, 0.65, 3.8, shade(steel, 1.18));
    if (vis.helm === 3 || vis.build === 'champion') m.box(lean, 0, headZ + 4.9, 5.8, 1.4, 2.3, p.accent, 0.2);
    if (vis.helm === 4) for (const side of [-1, 1]) m.crystal(lean, side * 3.3, headZ + 4.3, 1.3, 3.6, 0xd9d2b0);
    if (vis.shield) {
      const y = -breadth * 0.68 - 1;
      const outline: Vec3[] = [[-1.8, y, z + 20], [5.3, y, z + 20], [5.3, y, z + 13],
        [1.6, y, z + 9], [-1.8, y, z + 13]];
      m.poly(outline, shade(steel, 0.86));
      m.poly([[-1, y - 0.15, z + 19.2], [4.5, y - 0.15, z + 19.2], [4.5, y - 0.15, z + 13.4],
        [1.6, y - 0.15, z + 10.2], [-1, y - 0.15, z + 13.4]], shade(p.accent, 0.78));
      m.box(1.6, y - 0.3, z + 13.2, 0.75, 0.3, 5.5, shade(steel, 1.05));
      m.box(1.6, y - 0.4, z + 15.5, 1.6, 0.5, 1.6, steel);
    }
    if (vis.banner) {
      m.box(-5, 4, z + 8, 1.1, 1.1, 24, leather);
      m.poly([[-5, 4, z + 32], [-5, 12, z + 30], [-5, 10, z + 26], [-5, 4, z + 26]], p.accent);
    }
    // Trim is sewn across the shoulders, preserving a calm material palette.
    m.box(lean, 0, shoulderZ + 0.4, 3, breadth * 0.68, 0.5, mix(p.rim, p.accent, 0.25));
  };
  switch (vis.build) {
    case 'cavalry': {
      const coat = 0x927653;
      for (const fx of [-7, 7]) for (const side of [-1, 1]) {
        const stride = step * side * Math.sign(fx) * 2.8;
        const lift = side * Math.sign(fx) < 0 ? movement.liftLeft : movement.liftRight;
        const hoof: Vec3 = [fx + stride, side * 3.4, 1.2 + lift];
        const knee: Vec3 = [fx + stride * 0.4 + 1.2, side * 3.2, 5.7 + lift * 0.3];
        m.rounded(hoof[0] + 0.3, hoof[1], lift, 2.8, 2.3, 1.7, shade(leather, 0.65));
        m.limb(hoof, knee, 0.8, 1, coat);
        m.limb(knee, [fx, side * 3.1, 12], 1.2, 1.8, coat);
      }
      m.rounded(-1, 0, 8.5, 22, 10, 10, coat);
      m.limb([7, 0, 12], [10, 0, 23], 3.2, 2.5, 0x9e8462, 8);
      m.rounded(12.6, 0, 21, 10.4, 5.1, 5.6, 0xb69b74);
      m.rounded(17.1, 0, 21.2, 2.8, 4.3, 3.4, shade(coat, 0.78));
      for (const side of [-1, 1]) {
        m.crystal(10, side * 1.6, 25.3, 0.9, 3.1, coat);
        m.box(14.7, side * 2.15, 24.2, 0.8, 0.25, 0.55, 0x312b23);
        m.limb([16.3, side * 2.2, 22], [3.5, side * 2.1, 21], 0.12, 0.12, shade(leather, 0.75));
      }
      m.poly([[5.5, 0, 15], [7.7, 0, 25], [9.4, 0, 26], [7.1, 0, 16]], shade(leather, 0.55));
      m.box(0, 0, 16, 10, 11, 2, cloth);
      m.poly([[-10, 0, 15], [-19, 0, 7], [-12, 2, 7]], shade(leather, 0.7));
      humanoid(8);
      break;
    }
    case 'siege': {
      for (const x of [-10, 9]) for (const side of [-1, 1]) m.box(x, side * 8, 0, 6, 3.2, 7, leather, 0.8);
      m.box(0, 0, 5, 25, 14, 5, 0x8b724e);
      m.box(-4, 0, 10, 10, 10, 8, steel, 1);
      if (vis.weapon === 'gun') { m.box(8, 0, 12, 24, 6, 6, steel, 0.6); m.box(20.5, 0, 12.6, 0.5, 4.4, 4.4, EDGE); }
      else { m.box(1, 0, 17, 29, 3, 3.6, 0xb69a69); m.box(12, 0, 18, 6, 8, 3, steel); }
      m.box(-5, 0, 18, 5, 6, 0.6, p.accent);
      break;
    }
    case 'swarm': {
      for (const [x, y] of [[-7, -6], [5, -3], [-2, 7]]) {
        for (const side of [-1, 1]) m.box(x - step * side, y + side * 3, 0, 4, 2, 3, leather);
        m.box(x, y, 2, 7, 5, 5, cloth, 0.5);
        m.crystal(x + 4, y, 5, 3, 4, skin);
      }
      break;
    }
    case 'flier': {
      m.box(0, 0, 10, 13, 7, 8, cloth, 0.8);
      m.crystal(8, 0, 15, 4, 5, skin);
      for (const side of [-1, 1]) {
        const z = 14 + Math.cos(frame / 6 * Math.PI * 2) * 6;
        m.poly([[-4, side * 2, 15], [-8, side * 22, z], [2, side * 15, z + 2], [6, side * 3, 17]], shade(cloth, side === -1 ? 1.5 : 0.85));
        m.poly([[-4, side * 2, 15], [-8, side * 22, z], [-11, side * 12, z - 4]], shade(cloth, 0.68));
      }
      break;
    }
    case 'brute': humanoid(0, true); break;
    default: humanoid(0); break;
  }
  m.draw(g);
}
